import { z } from 'zod';
/**
 * Medium — die eine Ablage für alles, was vor Ort entsteht: Fotos, Videos,
 * Sprachnotizen, Dokumente. Vertrag laut docs/analysis/2026-09-07-konzept-medien.md
 * (Abschnitt 3) und dem Medien-Vertrag der Agenten.
 *
 * Grundsätze:
 *  - Das Original wird nie verändert (Entscheidung des Auftraggebers,
 *    2026-09-07). EXIF bleibt im Original und wird nur serverseitig gelesen.
 *  - Derivate (Vorschau, Thumbnail, Poster) sind aus dem Original jederzeit
 *    neu erzeugbar und tragen keine Metadaten.
 *  - Ein Medium gehört zu mindestens einer Kontextkante (Objekt, Anlage,
 *    Mangel, Begehung, Prüfung, Aufgaben-Erledigung, Projekt).
 *  - Löschen ist ein Soft-Delete mit Frist — Berichte und Audit behalten die
 *    Referenz, die Datei verschwindet erst nach Ablauf.
 */
export const MediumArt = z.enum(['foto', 'video', 'audio', 'dokument', 'plan']);
export const MEDIUM_ART_LABEL = {
    foto: 'Foto',
    video: 'Video',
    audio: 'Sprachnotiz',
    dokument: 'Dokument',
    plan: 'Plan',
};
export const MediumQuelle = z.enum(['kamera', 'datei', 'kunde_token', 'mail', 'import']);
export const MEDIUM_QUELLE_LABEL = {
    kamera: 'Kamera',
    datei: 'Datei',
    kunde_token: 'Kunde (QR-Zugang)',
    mail: 'E-Mail',
    import: 'Import',
};
/**
 * Aufbewahrungsklasse — steuert Löschvorschläge, nie automatische Löschung
 * (Konzept Abschnitt 7). Die Fristen selbst kommen später aus dem Katalog.
 */
export const Aufbewahrungsklasse = z.enum(['nachweis', 'arbeitsmaterial', 'temporaer']);
export const AUFBEWAHRUNGSKLASSE_LABEL = {
    nachweis: 'Nachweis',
    arbeitsmaterial: 'Arbeitsmaterial',
    temporaer: 'Vorübergehend',
};
/** Ohne Angabe beim Upload und ohne Katalogeintrag gilt diese Klasse. */
export const AUFBEWAHRUNGSKLASSE_DEFAULT = 'arbeitsmaterial';
/**
 * Welche Datei ausgeliefert wird. `original` gibt es immer; die anderen sind
 * Derivate, die der Server nach dem Upload erzeugt (Fotos sofort, Videos im
 * Hintergrund). `varianten` am Medium sagt, welche schon da sind.
 */
export const MediumVariante = z.enum(['original', 'thumb', 'vorschau', 'poster', 'web']);
export const MEDIUM_VARIANTE_LABEL = {
    original: 'Original',
    thumb: 'Vorschaubild (klein)',
    vorschau: 'Vorschau',
    poster: 'Standbild',
    web: 'Web-Fassung',
};
export const MediumStatus = z.enum(['aktiv', 'geloescht']);
export const MEDIUM_STATUS_LABEL = {
    aktiv: 'Aktiv',
    geloescht: 'Gelöscht',
};
/** Zweck-Schlüssel, wenn kein Katalogeintrag passt. */
export const MEDIUM_ZWECK_SONSTIGES = 'sonstiges';
/**
 * Wahrheitswert aus einem Query-String.
 *
 * `z.coerce.boolean()` wäre hier falsch und gefährlich: Es macht aus dem
 * String `"false"` ein `true` (jeder nicht-leere String ist wahr). Genau
 * dieses Feld entscheidet, ob ein Medium im Kundenkanal auftaucht — ein
 * ausdrückliches `sichtbarFuerKunde=false` darf es nicht sichtbar machen.
 * Dieselbe Linie wie `billable` in schemas/time-tracking.ts.
 */
const queryBoolean = z
    .union([z.boolean(), z.literal('true'), z.literal('false')])
    .transform((v) => v === true || v === 'true');
const kanteId = z.number().int().positive().nullable().optional();
const kanteIdQuery = z.coerce.number().int().positive().nullable().optional();
/**
 * Die Kontextkanten. Mindestens eine ist gesetzt — das prüft der Server, weil
 * ein Medium ohne Bezug in keiner Akte auftaucht und niemand es je wiederfände.
 */
export const mediumKontext = z.object({
    liegenschaftId: kanteId,
    anlageId: kanteId,
    mangelId: kanteId,
    begehungId: kanteId,
    pruefungId: kanteId,
    aufgabeErledigungId: kanteId,
    projectId: kanteId,
});
/**
 * Dieselben Kanten, aber aus einem Query-String gelesen: Dort ist jede Zahl
 * eine Zeichenkette. Upload-Felder und Listenfilter kommen als Query (die
 * Reihenfolge der Multipart-Teile darf keine Rolle spielen), deshalb brauchen
 * sie die Umwandlung — die Antwort trägt weiterhin echte Zahlen.
 */
export const mediumKontextQuery = z.object({
    liegenschaftId: kanteIdQuery,
    anlageId: kanteIdQuery,
    mangelId: kanteIdQuery,
    begehungId: kanteIdQuery,
    pruefungId: kanteIdQuery,
    aufgabeErledigungId: kanteIdQuery,
    projectId: kanteIdQuery,
});
export const MEDIUM_KONTEXT_KANTEN = [
    'liegenschaftId',
    'anlageId',
    'mangelId',
    'begehungId',
    'pruefungId',
    'aufgabeErledigungId',
    'projectId',
];
/** Hat der Kontext mindestens eine gesetzte Kante? */
export function hatKontextKante(kontext) {
    return MEDIUM_KONTEXT_KANTEN.some((k) => kontext[k] != null);
}
export const medium = mediumKontext.extend({
    id: z.string().uuid(),
    art: MediumArt,
    /** Schlüssel aus medienzwecke.json — oder `sonstiges`. */
    zweck: z.string().min(1).max(60),
    mimeType: z.string(),
    groesse: z.number().int().nonnegative(),
    sha256: z.string().length(64),
    breite: z.number().int().positive().nullable(),
    hoehe: z.number().int().positive().nullable(),
    dauerSek: z.number().nonnegative().nullable(),
    /** EXIF DateTimeOriginal bzw. Video-Erstellzeit — vom SERVER aus dem Original gelesen. */
    aufgenommenAm: z.string().datetime().nullable(),
    /** Kamera-Modell aus EXIF, wenn vorhanden. */
    geraet: z.string().nullable(),
    hochgeladenVonId: z.number().int().positive().nullable(),
    hochgeladenVonName: z.string().nullable(),
    hochgeladenAm: z.string().datetime(),
    quelle: MediumQuelle,
    sichtbarFuerKunde: z.boolean(),
    aufbewahrungsklasse: Aufbewahrungsklasse,
    /** Der Name, den die Datei beim Upload trug — nur zur Information. */
    originalName: z.string(),
    status: MediumStatus,
    /** Vom Server per `anzeigename()` berechnet — nie gespeichert. */
    anzeigename: z.string(),
    /** Welche Derivate vorhanden sind (`original` immer). */
    varianten: z.array(MediumVariante),
});
/**
 * Upload-Felder — als QUERY-STRING an POST /medien, damit die Reihenfolge der
 * Multipart-Teile keine Rolle spielt (Muster aus dem Dokumenten-Upload). Die
 * Datei kommt als Multipart-Feld `file`.
 */
export const mediumUploadFelder = mediumKontextQuery.extend({
    zweck: z.string().min(1).max(60),
    quelle: MediumQuelle.default('datei'),
    sichtbarFuerKunde: queryBoolean.default(false),
    /** Default aus dem Zweck-Katalog, sonst `arbeitsmaterial`. */
    aufbewahrungsklasse: Aufbewahrungsklasse.optional(),
});
export const mediumListQuery = mediumKontextQuery.extend({
    /**
     * Mehrere Befunde auf einmal — die Mängelkarte zeigt vierzig Zeilen und
     * soll dafür weder vierzig Anfragen stellen noch alle Medien des Objekts
     * holen und selbst aussieben. Komma-getrennte IDs, weil ein Query-String
     * keine Arrays kennt.
     */
    mangelIds: z
        .string()
        .optional()
        .transform((v) => v
        ? v
            .split(',')
            .map((t) => Number(t.trim()))
            .filter((n) => Number.isInteger(n) && n > 0)
        : undefined),
    zweck: z.string().optional(),
    art: MediumArt.optional(),
    status: MediumStatus.default('aktiv'),
    limit: z.coerce.number().int().min(1).max(500).default(100),
    offset: z.coerce.number().int().min(0).default(0),
});
export const updateMediumRequest = z
    .object({
    zweck: z.string().min(1).max(60),
    sichtbarFuerKunde: z.boolean(),
    aufbewahrungsklasse: Aufbewahrungsklasse,
})
    .partial();
export const mediumListResponse = z.object({ items: z.array(medium), total: z.number().int() });
/**
 * Kurzlebiges Datei-Token für `<img src>` / `<video src>` — ein Tag kann
 * keinen Bearer-Header schicken. `url` ist der fertige Pfad inklusive Token.
 */
export const mediumDateiTokenResponse = z.object({
    token: z.string(),
    url: z.string(),
    gueltigBis: z.string().datetime(),
});
/** Speicherkennzahlen für den Admin-Bereich (Konzept 3.5). */
export const medienStatistik = z.object({
    anzahl: z.number().int().nonnegative(),
    bytesGesamt: z.number().int().nonnegative(),
    jeLiegenschaft: z.array(z.object({
        liegenschaftId: z.number().int().positive(),
        bezeichnung: z.string(),
        anzahl: z.number().int().nonnegative(),
        bytes: z.number().int().nonnegative(),
    })),
    verwaisteVerzeichnisse: z.array(z.string()),
    geloeschtWartend: z.number().int().nonnegative(),
});
